import { create } from 'zustand';

interface AudioGenerationMessage {
    type?: string;
    blockHash?: string;
    data?: any; // Same structure as API preview/v2 response.data
    progress?: number;
    error?: string;
    message?: string;
}

type MessageHandler = (message: AudioGenerationMessage) => void;
type BuildProgressHandler = (message: any) => void;

interface IWebSocketStore {
    socket: WebSocket | null;
    connectionId: string | null;
    isConnecting: boolean;
    isConnected: boolean;
    messageHandlers: Map<string, MessageHandler>;
    buildProgressHandler: BuildProgressHandler | null;
    connect: () => void;
    disconnect: () => void;
    reconnect: () => void;
    isConnectionHealthy: () => boolean;
    getConnectionId: () => Promise<string | null>;
    setConnectionId: (connectionId: string) => void;
    resetConnectionId: () => void;
    subscribeToMessages: (blockHash: string, handler: MessageHandler) => () => void;
    unsubscribeFromMessages: (blockHash: string) => void;
    setBuildProgressHandler: (handler: BuildProgressHandler | null) => void;
}

export const useWebSocketStore = create<IWebSocketStore>()((set, get) => ({
    socket: null,
    connectionId: null,
    isConnecting: false,
    isConnected: false,
    messageHandlers: new Map(),
    buildProgressHandler: null,
    
    connect: () => {
        const state = get();
        
        // If already connected or connecting, don't create a new connection
        if (state.socket || state.isConnecting) {
            return;
        }
        
        set({ isConnecting: true });
        
        const ws = new WebSocket(process.env.REACT_APP_AUDIO_BUILDER_WEBSOCKET as string);
        
        ws.addEventListener('open', () => {
            console.log('WebSocket connected');
            set({ isConnected: true, isConnecting: false });
            // Request credentials
            ws.send(JSON.stringify({ action: 'socket', message: 'get credentials' }));
        });
        
        ws.addEventListener('message', (event) => {
            try {
                const message = JSON.parse(event.data);
                
                // Handle connectionId
                if (message.connectionId) {
                    console.log('Received connectionId:', message.connectionId);
                    set({ connectionId: message.connectionId });
                    return;
                }
                
                // Handle audio generation messages (has blockHash) - for preview
                if (message.blockHash) {
                    console.log('Received audio generation message:', message);
                    const state = get();
                    const handler = state.messageHandlers.get(message.blockHash);
                    if (handler) {
                        handler(message as AudioGenerationMessage);
                    } else {
                        console.warn('⚠️ No handler registered for blockHash:', message.blockHash);
                        console.warn('Registered handlers:', Array.from(state.messageHandlers.keys()));
                    }
                    return;
                }
                
                // Handle build/download progress messages (has progress field, audio_url, or data)
                if (message.progress !== undefined || message.audio_url || message.error || message.data) {
                    console.log('Received build/download progress:', message);
                    const state = get();
                    if (state.buildProgressHandler) {
                        state.buildProgressHandler(message);
                    }
                    return;
                }
            } catch (error) {
                console.error('Error parsing WebSocket message:', error);
            }
        });
        
        ws.addEventListener('close', () => {
            console.log('WebSocket disconnected');
            set({ socket: null, isConnected: false, isConnecting: false, connectionId: null });
        });
        
        ws.addEventListener('error', (error) => {
            console.error('WebSocket error:', error);
            set({ socket: null, isConnected: false, isConnecting: false });
        });
        
        set({ socket: ws });
    },
    
    disconnect: () => {
        const state = get();
        if (state.socket) {
            state.socket.close();
            set({ socket: null, isConnected: false, isConnecting: false, connectionId: null, messageHandlers: new Map() });
        }
    },
    
    reconnect: () => {
        const state = get();
        console.log('🔄 Reconnecting WebSocket...');
        
        // Disconnect existing connection if any
        if (state.socket) {
            state.socket.close();
        }
        
        // Reset state and reconnect
        set({ socket: null, isConnected: false, isConnecting: false, connectionId: null });
        
        // Trigger new connection
        get().connect();
    },
    
    isConnectionHealthy: () => {
        const state = get();
        return !!(
            state.socket && 
            state.isConnected && 
            state.connectionId &&
            state.socket.readyState === WebSocket.OPEN
        );
    },
    
    getConnectionId: async () => {
        const state = get();
        
        // If connection is healthy, return existing connectionId
        if (get().isConnectionHealthy()) {
            return state.connectionId;
        }
        
        // Connection is unhealthy, reconnect
        console.log('⚠️ Connection unhealthy, reconnecting...');
        get().reconnect();
        
        // Wait for new connectionId (with timeout)
        const maxWaitTime = 5000; // 5 seconds
        const checkInterval = 100; // Check every 100ms
        let elapsed = 0;
        
        while (elapsed < maxWaitTime) {
            await new Promise(resolve => setTimeout(resolve, checkInterval));
            elapsed += checkInterval;
            
            const currentState = get();
            if (currentState.connectionId && currentState.isConnected) {
                console.log('✅ Reconnected successfully');
                return currentState.connectionId;
            }
        }
        
        console.error('❌ Failed to reconnect within timeout');
        return null;
    },
    
    setConnectionId: (connectionId: string) => {
        set({ connectionId });
    },
    
    resetConnectionId: () => {
        set({ connectionId: null });
    },
    
    subscribeToMessages: (blockHash: string, handler: MessageHandler) => {
        const state = get();
        const newHandlers = new Map(state.messageHandlers);
        newHandlers.set(blockHash, handler);
        set({ messageHandlers: newHandlers });
        
        // Return unsubscribe function
        return () => {
            const currentState = get();
            const updatedHandlers = new Map(currentState.messageHandlers);
            updatedHandlers.delete(blockHash);
            set({ messageHandlers: updatedHandlers });
        };
    },
    
    unsubscribeFromMessages: (blockHash: string) => {
        const state = get();
        const newHandlers = new Map(state.messageHandlers);
        newHandlers.delete(blockHash);
        set({ messageHandlers: newHandlers });
    },
    
    setBuildProgressHandler: (handler: BuildProgressHandler | null) => {
        set({ buildProgressHandler: handler });
    }
}));

